// Produkt wyświetlany na listach i sliderach

import React, { useState, FC, useMemo, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { Check2, X } from 'react-bootstrap-icons';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useSelector } from 'store';
import {
  usePostProductQuantityDecrement,
  usePostProductQuantityIncrement,
  usePostProductQuantity
} from 'api';
import { IProductListItem, ITechnicalAttribute, IUnit } from 'api/types';
import { AddToCartButton, AddToShoppingListButton, FavoriteButton, CompareButton } from 'components/containers';
import { Availability, Counter, Link, Label } from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/components/containers/ProductItem/ProductItem.module.scss';

const DESCRIPTION_HEIGHT = 55;

// typ danych wejściowych
interface Props {
  product: IProductListItem;
  categoryId?: number;
  searchKeywords?: string;
  line?: boolean;
  minimalVariant?: boolean;
  imageStretch?: boolean;
  slide?: boolean;
  isSearch?: boolean;
  /** Gdy podane – używane zamiast url_link (np. karta produktu w TraderPanel) */
  productDetailUrl?: string;
}

const ProductItem: FC<Props> = ({
  product,
  line,
  minimalVariant,
  imageStretch,
  slide,
  categoryId,
  searchKeywords,
  isSearch,
  productDetailUrl
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { profile } = useSelector((state) => state.auth);

  // czy rozwinięte są szczegóły
  const [areProductDetails, setAreProductDetails] = useState(false);

  // aktualna ilość
  const [quantity, setQuantity] = useState(1);

  // aktualnie zmieniona ilość (sprzed walidacji)
  const [manuallyChangedQuantity, setManuallyChangedQuantity] = useState<number>();

  // ID aktualnej jednostki
  const [unitId, setUnitId] = useState<number>(
    product.product_unit_short_info?.default_unit_id || 0
  );

  // wysokośc opisu
  const [descriptionHeight, setDescriptionHeight] = useState(0);

  // aktualna jednostka
  const price = useMemo(
    () => product.product_price_short_info?.prices?.find((price) => price.unit_id === unitId),
    [unitId, product]
  );

  // uaktualnienie jednostki
  useEffect(() => {
    if (product.product_unit_short_info?.default_unit_id) {
      setUnitId(product.product_unit_short_info?.default_unit_id);
    }
  }, [product.product_unit_short_info]);

  // zmiana ilości po zmianie jednostki
  useEffect(() => {
    price &&
      setQuantity(
        product.product_unit_short_info?.units?.find((unit) => unitId === unit.unit_id)
          ?.minimal_quantity || 1
      );
  }, [price]);

  useEffect(() => {
    const selector = document.querySelector(`#product-description-${product.id}`);
    const height = selector?.getBoundingClientRect().height;

    if (height) {
      setDescriptionHeight(height);
    }
  }, [product.product_basic_short_info?.description, line]);

  // pobranie informacji o zmniejsonej ilości
  const { mutate: decrementQuantity, isLoading: isDecrementQuantityLoading } =
    usePostProductQuantityDecrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji o zwiększonej ilości
  const { mutate: incrementQuantity, isLoading: isIncrementQuantityLoading } =
    usePostProductQuantityIncrement(product.id, {
      onSuccess: (data) => {
        setQuantity(data.data.value);
      }
    });

  // pobranie informacji ilości
  const {
    mutate: updateQuantity,
    mutateAsync: updateQuantityAsync,
    isLoading: isUpdateQuantityLoading
  } = usePostProductQuantity(product.id, {
    onSuccess: (data) => {
      setQuantity(data.data.value);
    }
  });

  // funkcja renderująca dropdown jednostek
  const renderUnitSelector = useCallback(() => {
    const unit = product.product_unit_short_info?.units?.find((unit) => unitId === unit.unit_id);
    return (
      <div
        className={classnames(styles['unit-selector-wrapper'], {
          [styles.unitSelectorWrapperLine]: !!line,
          [styles.unitSelectorWrapperMinimal]: !!minimalVariant
        })}>
        {product.product_unit_short_info?.units &&
        product.product_unit_short_info.units.length > 1 &&
        profile?.role !== 'ROLE_OPEN_PROFILE' ? (
          <WiseSelect<IUnit>
            onChange={(unit) => unit?.unit_id && setUnitId(unit.unit_id)}
            value={unitId}
            options={product.product_unit_short_info?.units.map((unit) => ({
              value: unit.unit_id,
              label: `${unit.name} ${unit.converter !== 1 ? `(${unit.converter})` : ''}`,
              item: unit
            }))}
          />
        ) : (
          <>
            <Trans>Jednostka</Trans>: <strong>{unit?.name}</strong>
          </>
        )}
      </div>
    );
  }, [unitId, product]);

  // funkcja renderująca przycisk dodawania produktu do listy zakupowej (tylko tryb grid)
  const renderAddToShoppingListButton = useCallback(
    () => {
      if (profile?.role === 'ROLE_OPEN_PROFILE') return null;
      return (
        <div className={styles.shoppingListButtonWrapper}>
          <AddToShoppingListButton
            product_id={product.id}
            unit_id={product.product_unit_short_info?.default_unit_id}
            image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
            title={product.product_basic_short_info?.title}
            index={product.product_basic_short_info?.index}
            currency={price?.currency}
            unit={price}
            quantity={quantity}
          />
          <div className={styles.shoppingListIconGroup}>
            {product.product_shopping_list_short_info && (
              <FavoriteButton
                productId={product.id}
                unitId={product.product_unit_short_info?.default_unit_id || 0}
                inFavourite={product.product_shopping_list_short_info.in_favourite_shopping_list}
              />
            )}
            <CompareButton
              productId={product.id}
              title={product.product_basic_short_info?.title}
              image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
            />
          </div>
        </div>
      );
    },
    [profile, quantity, price, product]
  );

  // funkcja renderująca wartość atrybutu
  const renderTechnicalAttributeValue = (item: ITechnicalAttribute) =>
    item.type === 'boolean' ? (
      item.value ? (
        <Check2 className={styles.green} />
      ) : (
        <X className={styles.red} />
      )
    ) : (
      item.value
    );

  const Title = () => {
    return (
      <div
        className={classnames(styles['product-title'], {
          [styles.productTitleLine]: !!line
        })}>
        <Link
          className={styles.title}
          to={productDetailUrl ?? `/${product.product_basic_short_info?.url_link}`}
          state={productDetailUrl ? undefined : { categoryId, searchKeywords }}>
          <span itemProp="name">{product.product_basic_short_info?.title}</span>
        </Link>
        <meta
          itemProp="url"
          content={window.location.origin + product.product_basic_short_info?.url_link}
        />
        <span>
          {t('INDEX')}: {product.product_basic_short_info?.index}
        </span>
        <span>
          {product.product_basic_short_info?.producer_name && (
            <span itemScope itemProp="brand" itemType="http://schema.org/Brand">
              {t('Producent')}:{' '}
              <strong itemProp="name">{product.product_basic_short_info?.producer_name}</strong>
            </span>
          )}
        </span>

        {line && (
          <>
            <div
              id={`product-description-${product.id}`}
              className={styles['product-description']}
              style={
                !areProductDetails && descriptionHeight >= DESCRIPTION_HEIGHT
                  ? { height: DESCRIPTION_HEIGHT, overflow: 'hidden' }
                  : {}
              }
              dangerouslySetInnerHTML={{
                __html: product.product_basic_short_info?.description || ''
              }}
            />
            {descriptionHeight >= DESCRIPTION_HEIGHT && !areProductDetails && <div>...</div>}

            <div className={styles['product-params']}>
              {areProductDetails && (
                <ul>
                  {product.product_basic_short_info?.technical_attributes?.map((attribute) => (
                    <li key={attribute.name}>
                      {attribute.name}: <strong>{renderTechnicalAttributeValue(attribute)}</strong>
                    </li>
                  ))}
                </ul>
              )}

              {descriptionHeight >= DESCRIPTION_HEIGHT && (
                <button onClick={() => setAreProductDetails(!areProductDetails)}>
                  {areProductDetails ? t('Zwiń szczegóły') : t('Rozwiń szczegóły')}
                </button>
              )}
            </div>
          </>
        )}

        {minimalVariant && (
          <div
            className={classnames(styles.priceWrapper, {
              [styles.priceWrapperLine]: !!line
            })}>
            {price?.old_price_net_formatted && (
              <div
                className={classnames(styles.old, {
                  [styles.oldLine]: !!line
                })}>
                {price.old_price_net_formatted} {price.currency}
              </div>
            )}
            <div className={styles.net}>
              <strong>
                {price?.price_net_formatted} {price?.currency}
              </strong>{' '}
              {t('netto')}
            </div>
            <div className={styles.gross}>
              {price?.price_gross_formatted} {price?.currency} {t('brutto')}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={classnames(
        styles.componentWrapper,
        {
          [styles.lineWrapper]: !!line,
          [styles.slide]: !!slide
        },
        'StylePath-Components-Containers-ProductItem'
      )}
      itemProp="item"
      itemScope
      itemType="http://schema.org/Product">
      <div
        className={classnames(styles.imageTitleWrapper, {
          [styles.imageStretch]: imageStretch,
          [styles.imageTitleWrapperLine]: !!line
        })}>
        <Link
          to={productDetailUrl ?? `/${product.product_basic_short_info?.url_link}`}
          state={productDetailUrl ? undefined : { categoryId, searchKeywords }}
          className={classnames(styles['product-image'], {
            [styles.productImageLine]: !!line,
            [styles.productImageMinimal]: !!minimalVariant
          })}
          style={{
            backgroundImage: `url(${product.product_image_formatted_short_info?.images?.[0]?.min})`,
            backgroundSize: 'contain'
          }}>
          {product.product_basic_short_info?.labels?.map((label) => (
            <Label key={label.type} label={label} />
          ))}
        </Link>
        <meta
          itemProp="image"
          content={product.product_image_formatted_short_info?.images?.[0]?.min}
        />
        {minimalVariant && <Title />}
      </div>

      <div
        className={classnames(styles['product-details'], {
          [styles.productDetailsLine]: !!line,
          [styles.noActions]: profile?.role === 'ROLE_OPEN_PROFILE'
        })}>
        {!minimalVariant && <Title />}

        <div
          className={classnames(styles['product-actions'], {
            [styles.productActionsLine]: !!line,
            [styles.isSearch]: !!isSearch
          })}>
          {!line && !minimalVariant && renderUnitSelector()}

          {profile?.role !== 'ROLE_OPEN_PROFILE' && !minimalVariant && (
            <div
              className={classnames(styles.priceWrapper, {
                [styles.priceWrapperLine]: !!line
              })}>
              {price?.old_price_net_formatted && (
                <div
                  className={classnames(styles.old, {
                    [styles.oldLine]: !!line
                  })}>
                  {price?.old_price_net_formatted} {price?.currency}
                </div>
              )}
              <div className={styles.net}>
                <strong>
                  {price?.price_net_formatted} {price?.currency}
                </strong>{' '}
                {t('netto')}
              </div>
              <div className={styles.gross}>
                {price?.price_gross_formatted} {price?.currency} {t('brutto')}
              </div>
            </div>
          )}

          {!minimalVariant && (
            <div
              className={classnames(styles['availability-wrapper'], {
                [styles.availabilityWrapperLine]: !!line
              })}>
              {line && profile?.role !== 'ROLE_OPEN_PROFILE' && product.product_shopping_list_short_info && (
                <FavoriteButton
                  productId={product.id}
                  unitId={product.product_unit_short_info?.default_unit_id || 0}
                  inFavourite={product.product_shopping_list_short_info.in_favourite_shopping_list}
                />
              )}
              {line && (
                <CompareButton
                  productId={product.id}
                  title={product.product_basic_short_info?.title}
                  image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
                />
              )}
              {line && profile?.role !== 'ROLE_OPEN_PROFILE' && (
                <AddToShoppingListButton
                  product_id={product.id}
                  unit_id={product.product_unit_short_info?.default_unit_id}
                  image={product.product_image_formatted_short_info?.images?.[0]?.thumb}
                  title={product.product_basic_short_info?.title}
                  index={product.product_basic_short_info?.index}
                  currency={price?.currency}
                  unit={price}
                  quantity={quantity}
                />
              )}
              {product.product_stock_formatted_short_info && (
                <Availability stock={product.product_stock_formatted_short_info} />
              )}
            </div>
          )}

          <div
            className={classnames(styles.tools, {
              [styles.toolsLine]: !!line
            })}>
            {(line || minimalVariant) && renderUnitSelector()}

            <div
              className={classnames(styles['counter-wrapper'], {
                [styles['counter-wrapper-line']]: line
              })}>
              {profile?.role !== 'ROLE_OPEN_PROFILE' ? (
                <Counter
                  onChange={(value) => updateQuantity({ value, unit_id: unitId })}
                  onDecrease={() => decrementQuantity({ value: quantity, unit_id: unitId })}
                  onIncrease={() => incrementQuantity({ value: quantity, unit_id: unitId })}
                  value={quantity}
                  onChangeValue={(isChanged, newValue) => setManuallyChangedQuantity(newValue)}
                  disabled={
                    isDecrementQuantityLoading ||
                    isIncrementQuantityLoading ||
                    isUpdateQuantityLoading
                  }
                />
              ) : (
                <Link
                  to={`/login?return_url=${encodeURIComponent(
                    location.pathname + location.search
                  )}`}
                  className={styles.loginButton}>
                  <Trans>zaloguj</Trans>
                </Link>
              )}
            </div>

            <AddToCartButton
              isQuantityChanges={!!manuallyChangedQuantity && manuallyChangedQuantity !== quantity}
              updateQuantity={async () => {
                const newQuantity = manuallyChangedQuantity
                  ? await updateQuantityAsync({
                      value: manuallyChangedQuantity,
                      unit_id: unitId
                    })
                  : undefined;

                return newQuantity?.data.value === manuallyChangedQuantity
                  ? manuallyChangedQuantity
                  : undefined;
              }}
              quantity={quantity}
              unitId={unitId}
              productId={product.id}
              disabled={
                profile?.role === 'ROLE_OPEN_PROFILE' ||
                isDecrementQuantityLoading ||
                isIncrementQuantityLoading
              }
            />
          </div>

          {!line && !minimalVariant && renderAddToShoppingListButton()}
        </div>
      </div>
    </div>
  );
};

export default ProductItem;
